import { type RefObject, useEffect, useRef } from "react";

const MIN_DISTANCE = 60;

/** Which way a finger moved, or null when it was a tap or mostly vertical. */
export function swipeDirection(dx: number, dy: number): "left" | "right" | null {
  if (Math.abs(dx) < MIN_DISTANCE || Math.abs(dx) < 2 * Math.abs(dy)) return null;
  return dx < 0 ? "left" : "right";
}

/** Horizontal touch swipes on `ref` (spec08 FR11: previous / next calendar period). */
export function useSwipe(
  ref: RefObject<HTMLElement | null>,
  onSwipe: (direction: "left" | "right") => void,
) {
  const handler = useRef(onSwipe);
  handler.current = onSwipe;
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let start: { x: number; y: number } | null = null;
    const down = (event: PointerEvent) => {
      start = event.pointerType === "touch" ? { x: event.clientX, y: event.clientY } : null;
    };
    const up = (event: PointerEvent) => {
      if (!start) return;
      const direction = swipeDirection(event.clientX - start.x, event.clientY - start.y);
      start = null;
      if (direction) handler.current(direction);
    };
    const cancel = () => {
      start = null;
    };
    el.addEventListener("pointerdown", down);
    el.addEventListener("pointerup", up);
    el.addEventListener("pointercancel", cancel);
    return () => {
      el.removeEventListener("pointerdown", down);
      el.removeEventListener("pointerup", up);
      el.removeEventListener("pointercancel", cancel);
    };
  }, [ref]);
}
